import { useMemo, useRef, useState, type DragEvent, type ReactNode } from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import Link from '@mui/material/Link';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Stepper from '@mui/material/Stepper';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import {
  encodeBytes,
  isActiveJob,
  isFinalJob,
  links,
  useDownloadJobArtifact,
  useJob,
  useLeadImportTemplate,
  type Job,
} from '@eis/core';
import { JobProgress, usePermissions } from '@eis/patterns';
import { dataGridDefaults, dataGridSx } from '@eis/theme';
import { insurancePermissions as p } from '../../../permissions';
import { useImportLeads } from '../api/leads';
import { findingMessage, isWarning, type ImportFinding } from '../model/import-findings';
import { refusalMessageOf } from '../model/refusals';
import { JsonPasteImport } from './JsonPasteImport';

type TemplateFormat = 'XLSX' | 'CSV' | 'JSON_SCHEMA';
type FileFormat = 'CSV' | 'XLSX' | 'JSON';

interface ChosenFile {
  readonly name: string;
  readonly format: FileFormat;
  readonly content: string;
}

const STEPS = ['template', 'file', 'check', 'import', 'result'] as const;

/** The format the service reads a file as, from its name (SDD-020 §7.3). */
export function fileFormatOf(name: string): FileFormat | undefined {
  const lower = name.toLowerCase();
  if (lower.endsWith('.csv')) return 'CSV';
  if (lower.endsWith('.xlsx')) return 'XLSX';
  if (lower.endsWith('.json')) return 'JSON';
  return undefined;
}

async function readFile(file: File): Promise<ChosenFile | undefined> {
  const format = fileFormatOf(file.name);
  if (format === undefined) return undefined;
  const bytes = new Uint8Array(await file.arrayBuffer());
  return { name: file.name, format, content: encodeBytes(bytes) };
}

export interface ImportLeadsWizardProps {
  readonly open: boolean;
  readonly onClose: () => void;
  /** The group policy whose member leads are imported; omitted on the leads list (it asks). */
  readonly parentPolicyCode?: string;
}

/**
 * Lead import as five steps (SDD-020 §7.1, §9): the template derived from the tarif version, the
 * file (CSV, XLSX, JSON — the SDD-013 JSON paste survives under "Erweitert"), the dry run's check
 * with every finding and the error file, the choice of SKIP (default) or ABORT, and the result
 * with the created leads. Both runs are jobs: a big file can be closed after step 2 and continues
 * in the jobs tray. Nothing is parsed in the browser — the service reads the file.
 */
export function ImportLeadsWizard({
  open,
  onClose,
  parentPolicyCode,
}: ImportLeadsWizardProps): ReactNode {
  const { t, i18n } = useTranslation('insurance');
  const { can } = usePermissions();
  const locale = i18n.language.startsWith('en') ? 'en' : 'de';
  const [step, setStep] = useState(0);
  const [contextKind, setContextKind] = useState<'member' | 'standalone'>('member');
  const [groupPolicy, setGroupPolicy] = useState(parentPolicyCode ?? '');
  const [tarifErn, setTarifErn] = useState('');
  const [templateFormat, setTemplateFormat] = useState<TemplateFormat>('XLSX');
  const [file, setFile] = useState<ChosenFile | null>(null);
  const [unsupported, setUnsupported] = useState(false);
  const [dryJobId, setDryJobId] = useState<string>();
  // A replay's `note` comes with the create's answer only (I-6); the job read does not repeat it.
  const [dryNote, setDryNote] = useState('');
  const fileInput = useRef<HTMLInputElement | null>(null);
  const [runJobId, setRunJobId] = useState<string>();
  const [fileRefusal, setFileRefusal] = useState<readonly ImportFinding[]>([]);
  const [onError, setOnError] = useState<'SKIP' | 'ABORT'>('SKIP');
  const [registerPartners, setRegisterPartners] = useState(false);

  const template = useLeadImportTemplate();
  const importLeads = useImportLeads();
  const dryJob = useJob('insurance', dryJobId);
  const runJob = useJob('insurance', runJobId);

  const fixedParent = parentPolicyCode !== undefined;
  const memberContext = fixedParent || contextKind === 'member';
  const context = memberContext
    ? { parentPolicyCode: groupPolicy.trim() }
    : { tarifErn: tarifErn.trim() };
  const contextReady = memberContext ? groupPolicy.trim() !== '' : tarifErn.trim() !== '';
  const label = t('leads.wizard.title') + (memberContext ? ` ${groupPolicy.trim()}` : '');

  const reset = (): void => {
    setStep(0);
    setFile(null);
    setUnsupported(false);
    setDryJobId(undefined);
    setDryNote('');
    setRunJobId(undefined);
    setFileRefusal([]);
    setOnError('SKIP');
    setRegisterPartners(false);
    template.reset();
    importLeads.reset();
  };

  const close = (): void => {
    reset();
    onClose();
  };

  const choose = async (picked: File | undefined): Promise<void> => {
    if (picked === undefined) return;
    const read = await readFile(picked);
    setUnsupported(read === undefined);
    setFile(read ?? null);
    setFileRefusal([]);
  };

  const check = (): void => {
    if (file === null) return;
    importLeads.mutate(
      {
        ...context,
        dryRun: true,
        onError: 'SKIP',
        file: { format: file.format, filename: file.name, content: file.content },
        wait: '2s',
        locale,
        label: `${label} (${t('leads.wizard.steps.check')})`,
      },
      {
        onSuccess: (response) => {
          const job = response.importJob;
          if (job === undefined) return;
          const refused = (job.leadImport?.fileFindings ?? []).filter((f) => !isWarning(f));
          if (isFinalJob(job) && refused.length > 0) {
            setFileRefusal(refused);
            return;
          }
          setDryJobId(job.id);
          setDryNote(job.note ?? '');
          setStep(2);
        },
      },
    );
  };

  const run = (): void => {
    if (dryJobId === undefined) return;
    importLeads.mutate(
      {
        ...context,
        fromDryRunJobId: dryJobId,
        onError,
        registerPartners,
        wait: '2s',
        locale,
        label,
      },
      {
        onSuccess: (response) => {
          setRunJobId(response.importJob?.id);
          setStep(4);
        },
      },
    );
  };

  const checked = dryJob.data;
  const counts = checked?.leadImport?.counts;
  const valid = counts?.valid ?? 0;

  return (
    <Dialog
      open={open}
      onClose={close}
      fullWidth
      maxWidth="md"
      aria-labelledby="import-leads-title"
    >
      <DialogTitle id="import-leads-title">{t('leads.wizard.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <Stepper activeStep={step} alternativeLabel>
            {STEPS.map((key) => (
              <Step key={key}>
                <StepLabel>{t(`leads.wizard.steps.${key}`)}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {importLeads.isError && (
            <Alert severity="error" data-testid="wizard-refusal">
              {refusalMessageOf(importLeads.error)}
            </Alert>
          )}

          {step === 0 && (
            <Stack spacing={2}>
              {!fixedParent && (
                <Stack spacing={1}>
                  <FormLabel id="import-context">{t('leads.wizard.context.label')}</FormLabel>
                  <RadioGroup
                    row
                    aria-labelledby="import-context"
                    value={contextKind}
                    onChange={(event) => {
                      setContextKind(event.target.value === 'standalone' ? 'standalone' : 'member');
                    }}
                  >
                    <FormControlLabel
                      value="member"
                      control={<Radio size="small" />}
                      label={t('leads.wizard.context.member')}
                    />
                    <FormControlLabel
                      value="standalone"
                      control={<Radio size="small" />}
                      label={t('leads.wizard.context.standalone')}
                    />
                  </RadioGroup>
                  {contextKind === 'member' ? (
                    <TextField
                      size="small"
                      label={t('leads.wizard.context.groupPolicy')}
                      value={groupPolicy}
                      onChange={(event) => {
                        setGroupPolicy(event.target.value);
                      }}
                      sx={{ maxWidth: 320 }}
                    />
                  ) : (
                    <TextField
                      size="small"
                      label={t('leads.wizard.context.tarif')}
                      value={tarifErn}
                      onChange={(event) => {
                        setTarifErn(event.target.value);
                      }}
                      sx={{ maxWidth: 420 }}
                    />
                  )}
                </Stack>
              )}
              <Typography variant="body2" color="text.secondary">
                {t('leads.wizard.template.description')}
              </Typography>
              <Stack spacing={1}>
                <FormLabel id="template-format">{t('leads.wizard.template.format')}</FormLabel>
                <RadioGroup
                  aria-labelledby="template-format"
                  value={templateFormat}
                  onChange={(event) => {
                    const next = event.target.value;
                    setTemplateFormat(next === 'CSV' || next === 'JSON_SCHEMA' ? next : 'XLSX');
                  }}
                >
                  <FormControlLabel
                    value="XLSX"
                    control={<Radio size="small" />}
                    label={t('leads.wizard.template.xlsx')}
                  />
                  <FormControlLabel
                    value="CSV"
                    control={<Radio size="small" />}
                    label={t('leads.wizard.template.csv')}
                  />
                  <FormControlLabel
                    value="JSON_SCHEMA"
                    control={<Radio size="small" />}
                    label={t('leads.wizard.template.schema')}
                  />
                </RadioGroup>
              </Stack>
              <Box>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={!contextReady || template.isPending}
                  onClick={() => {
                    // The hook starts the download itself (the answer is a link to follow).
                    template.mutate({ ...context, format: templateFormat, locale });
                  }}
                >
                  {t('leads.wizard.template.download')}
                </Button>
              </Box>
              {template.data !== undefined && (
                <Alert severity="success" data-testid="template-downloaded">
                  {t('leads.wizard.template.downloaded', {
                    scope: template.data.context ?? '',
                    date: template.data.effectiveDate ?? '',
                    count: template.data.columns?.length ?? 0,
                  })}
                </Alert>
              )}
              {template.isError && (
                <Alert severity="error">{refusalMessageOf(template.error)}</Alert>
              )}
            </Stack>
          )}

          {step === 1 && (
            <Stack spacing={2}>
              <Typography variant="body2" color="text.secondary">
                {t('leads.wizard.file.description')}
              </Typography>
              <Box
                data-testid="file-drop"
                onDragOver={(event: DragEvent) => {
                  event.preventDefault();
                }}
                onDrop={(event: DragEvent) => {
                  event.preventDefault();
                  void choose(event.dataTransfer.files[0]);
                }}
                sx={{
                  border: 1,
                  borderStyle: 'dashed',
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 3,
                  textAlign: 'center',
                }}
              >
                <Stack spacing={1} sx={{ alignItems: 'center' }}>
                  <UploadFileOutlinedIcon color="action" />
                  <Typography variant="body2">{t('leads.wizard.file.drop')}</Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      fileInput.current?.click();
                    }}
                  >
                    {t('leads.wizard.file.choose')}
                  </Button>
                  <input
                    type="file"
                    hidden
                    ref={fileInput}
                    accept=".csv,.xlsx,.json,text/csv,application/json,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    data-testid="file-input"
                    onChange={(event) => {
                      void choose(event.target.files?.[0]);
                    }}
                  />
                  {file !== null && (
                    <Typography variant="body2" data-testid="chosen-file">
                      {t('leads.wizard.file.chosen', { name: file.name, format: file.format })}
                    </Typography>
                  )}
                </Stack>
              </Box>
              {unsupported && (
                <Alert severity="warning">{t('leads.wizard.file.unsupported')}</Alert>
              )}
              {fileRefusal.length > 0 && (
                <Alert severity="error" data-testid="file-refusal">
                  <Typography variant="body2" sx={{ fontWeight: 'fontWeightMedium' }}>
                    {t('leads.wizard.file.refused')}
                  </Typography>
                  {fileRefusal.map((finding, index) => (
                    <Typography key={`${finding.code ?? ''}-${String(index)}`} variant="body2">
                      {findingMessage(t, finding)}
                    </Typography>
                  ))}
                </Alert>
              )}
              {memberContext && groupPolicy.trim() !== '' && (
                <Accordion disableGutters variant="outlined">
                  <AccordionSummary expandIcon={<ExpandMoreOutlinedIcon />}>
                    <Typography variant="body2">{t('leads.wizard.file.advanced')}</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <JsonPasteImport parentPolicyCode={groupPolicy.trim()} />
                  </AccordionDetails>
                </Accordion>
              )}
            </Stack>
          )}

          {step === 2 && <CheckStep job={checked} note={dryNote} />}

          {step === 3 && (
            <Stack spacing={1.5}>
              <RadioGroup
                aria-label={t('leads.wizard.steps.import')}
                value={onError}
                onChange={(event) => {
                  setOnError(event.target.value === 'ABORT' ? 'ABORT' : 'SKIP');
                }}
              >
                <FormControlLabel
                  value="SKIP"
                  control={<Radio size="small" />}
                  label={t('leads.wizard.import.skip', { count: valid })}
                />
                <FormControlLabel
                  value="ABORT"
                  control={<Radio size="small" />}
                  label={t('leads.wizard.import.abort')}
                />
              </RadioGroup>
              {onError === 'ABORT' && (
                <Typography variant="caption" color="text.secondary">
                  {t('leads.wizard.import.abortHint')}
                </Typography>
              )}
              {can(p.partners.create) && (
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={registerPartners}
                      onChange={(event) => {
                        setRegisterPartners(event.target.checked);
                      }}
                    />
                  }
                  label={t('leads.wizard.import.registerPartners')}
                />
              )}
            </Stack>
          )}

          {step === 4 && <ResultStep job={runJob.data} />}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>
          {step === 4 ? t('leads.wizard.actions.done') : t('leads.wizard.actions.close')}
        </Button>
        {step > 0 && step < 4 && (
          <Button
            onClick={() => {
              setStep(step === 2 ? 1 : step - 1);
            }}
          >
            {t('leads.wizard.actions.back')}
          </Button>
        )}
        {step === 0 && (
          <Button
            variant="contained"
            disabled={!contextReady}
            onClick={() => {
              setStep(1);
            }}
          >
            {template.data === undefined
              ? t('leads.wizard.template.skip')
              : t('leads.wizard.actions.next')}
          </Button>
        )}
        {step === 1 && (
          <Button
            variant="contained"
            disabled={file === null || importLeads.isPending}
            onClick={check}
          >
            {t('leads.wizard.file.check')}
          </Button>
        )}
        {step === 2 && (
          <Button
            variant="contained"
            disabled={checked?.state !== 'SUCCEEDED' || valid === 0}
            onClick={() => {
              setStep(3);
            }}
          >
            {t('leads.wizard.actions.next')}
          </Button>
        )}
        {step === 3 && (
          <Button variant="contained" disabled={importLeads.isPending} onClick={run}>
            {t('leads.wizard.import.start')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

/** Step 3: the dry run — its progress while it runs, then the counts and every finding. */
function CheckStep({
  job,
  note,
}: {
  readonly job: Job | undefined;
  readonly note: string;
}): ReactNode {
  const { t } = useTranslation('insurance');
  const download = useDownloadJobArtifact();
  const [onlyErrors, setOnlyErrors] = useState(false);
  const result = job?.leadImport;
  const findings = useMemo(
    () => (result?.findings ?? []).filter((finding) => !onlyErrors || !isWarning(finding)),
    [result?.findings, onlyErrors],
  );
  const columns: GridColDef<ImportFinding & { id: string }>[] = [
    { field: 'row', headerName: t('leads.wizard.check.columns.row'), width: 70, type: 'number' },
    { field: 'column', headerName: t('leads.wizard.check.columns.column'), width: 80 },
    { field: 'label', headerName: t('leads.wizard.check.columns.label'), width: 160 },
    {
      field: 'message',
      headerName: t('leads.wizard.check.columns.message'),
      flex: 1,
      minWidth: 220,
      valueGetter: (_value, row) => findingMessage(t, row),
    },
    { field: 'value', headerName: t('leads.wizard.check.columns.value'), width: 140 },
  ];

  if (job === undefined || isActiveJob(job)) {
    return (
      <Stack spacing={1.5} data-testid="check-running">
        <Typography variant="body2">{t('leads.wizard.check.running')}</Typography>
        {job !== undefined && <JobProgress job={job} />}
        <Alert severity="info">{t('leads.wizard.check.background')}</Alert>
      </Stack>
    );
  }
  if (job.state !== 'SUCCEEDED') {
    return <Alert severity="error">{job.error?.message ?? t('leads.wizard.failed')}</Alert>;
  }
  const counts = result?.counts;
  const warnings = (result?.fileFindings ?? []).filter(isWarning);

  return (
    <Stack spacing={1.5} data-testid="check-result">
      {note !== '' && (
        <Alert severity="info" data-testid="import-note">
          {note}
        </Alert>
      )}
      {warnings.map((finding, index) => (
        <Alert
          key={`${finding.code ?? ''}-${String(index)}`}
          severity="warning"
          data-testid="version-warning"
        >
          {findingMessage(t, finding)}
        </Alert>
      ))}
      <Stack
        direction="row"
        spacing={1}
        useFlexGap
        sx={{ flexWrap: 'wrap' }}
        data-testid="check-summary"
      >
        <Chip
          size="small"
          color="success"
          variant="outlined"
          label={t('leads.wizard.check.summary.valid', { count: counts?.valid ?? 0 })}
        />
        <Chip
          size="small"
          color="error"
          variant="outlined"
          label={t('leads.wizard.check.summary.failed', { count: counts?.failed ?? 0 })}
        />
        {(counts?.warnings ?? 0) > 0 && (
          <Chip
            size="small"
            color="warning"
            variant="outlined"
            label={t('leads.wizard.check.summary.warnings', { count: counts?.warnings ?? 0 })}
          />
        )}
      </Stack>
      {(result?.findings ?? []).length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t('leads.wizard.check.noFindings')}
        </Typography>
      ) : (
        <>
          <Stack
            direction="row"
            spacing={2}
            sx={{ alignItems: 'center', justifyContent: 'space-between' }}
          >
            <Typography variant="subtitle2" component="h3">
              {t('leads.wizard.check.findings')}
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={onlyErrors}
                  onChange={(event) => {
                    setOnlyErrors(event.target.checked);
                  }}
                />
              }
              label={t('leads.wizard.check.onlyErrors')}
            />
          </Stack>
          <DataGrid
            {...dataGridDefaults}
            sx={dataGridSx}
            autoHeight
            rows={findings.map((finding, index) => ({ ...finding, id: String(index) }))}
            columns={columns}
            aria-label={t('leads.wizard.check.findings')}
            hideFooter={findings.length <= 100}
            disableColumnFilter
          />
          {result?.findingsTruncated === true && (
            <Typography variant="caption" color="text.secondary">
              {t('leads.wizard.check.truncated')}
            </Typography>
          )}
        </>
      )}
      {result?.errorFileAvailable === true && (
        <Box>
          <Button
            size="small"
            variant="outlined"
            onClick={() => void download('insurance', job.id ?? '', 'errors')}
          >
            {t('leads.wizard.check.errorFile')}
          </Button>
        </Box>
      )}
    </Stack>
  );
}

/** Step 5: what the import did — created leads as links, skipped and failed rows, the error file. */
function ResultStep({ job }: { readonly job: Job | undefined }): ReactNode {
  const { t } = useTranslation('insurance');
  const navigate = useNavigate();
  const download = useDownloadJobArtifact();
  if (job === undefined || isActiveJob(job)) {
    return (
      <Stack spacing={1.5} data-testid="result-running">
        <Typography variant="body2">{t('leads.wizard.result.running')}</Typography>
        {job !== undefined && <JobProgress job={job} />}
        <Alert severity="info">{t('leads.wizard.check.background')}</Alert>
      </Stack>
    );
  }
  if (job.state !== 'SUCCEEDED') {
    return <Alert severity="error">{job.error?.message ?? t('leads.wizard.failed')}</Alert>;
  }
  const result = job.leadImport;
  const counts = result?.counts;
  const created = result?.createdLeadCodes ?? [];
  const aborted =
    result?.onError === 'ABORT' && (counts?.created ?? 0) === 0 && (counts?.failed ?? 0) > 0;
  return (
    <Stack spacing={1.5} data-testid="wizard-result">
      {job.note !== undefined && job.note !== '' && <Alert severity="info">{job.note}</Alert>}
      {aborted && <Alert severity="warning">{t('leads.wizard.result.aborted')}</Alert>}
      <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
        <Chip
          size="small"
          color="success"
          variant="outlined"
          label={t('leads.wizard.result.created', { count: counts?.created ?? 0 })}
        />
        <Chip
          size="small"
          variant="outlined"
          label={t('leads.wizard.result.skipped', { count: counts?.skipped ?? 0 })}
        />
        <Chip
          size="small"
          color="error"
          variant="outlined"
          label={t('leads.wizard.result.failed', { count: counts?.failed ?? 0 })}
        />
      </Stack>
      {created.length > 0 && (
        <Stack spacing={0.5}>
          <Typography variant="subtitle2" component="h3">
            {t('leads.wizard.result.createdLeads')}
          </Typography>
          <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: 'wrap' }}>
            {created.map((code) => (
              <Link
                key={code}
                href={links.insurance.lead(code)}
                variant="body2"
                onClick={(event) => {
                  event.preventDefault();
                  void navigate({ href: links.insurance.lead(code) });
                }}
              >
                {code}
              </Link>
            ))}
          </Stack>
        </Stack>
      )}
      {result?.errorFileAvailable === true && (
        <Box>
          <Button
            size="small"
            variant="outlined"
            onClick={() => void download('insurance', job.id ?? '', 'errors')}
          >
            {t('leads.wizard.check.errorFile')}
          </Button>
        </Box>
      )}
    </Stack>
  );
}
